/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { NoteService } from 'app/core/services/note-service';
import { IScrollTarget } from 'app/shared/attributes/ess-scroll-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { NoteHistory } from 'app/core/components/notes/note-history'
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class NotesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: INotesConfig = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openMemberSummary = openMemberSummary;
    protected openCaseSummary = openCaseSummary;

    protected notes: ReadonlyArray<Core.Note> = [];

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected canAddNote: boolean = false;

    protected validate = validate;
    protected isValid: boolean = false;

    ////****************** Element References ***************************************////

    protected $pager: EssPagerCustomElement | any;
    protected $gridScroll!: IScrollTarget;
    protected $noteHistoryModal!: IPopup;
    protected $noteHistory!: NoteHistory;
    protected $addNoteModal!: IPopup;
    protected $form!: IForm;
    protected $noteForm!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: NoteService,
        private readonly context: HouseholdCasePageContext,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.service.configure(this.config);
        this.loadNotes();
    }

    ////****************** Events ****************************************************////

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$addNoteModal.close();
        if(detail.form.reset) detail.form.reset();
        this.reload();
    }

    ////****************** Functions ************************************************////

    public async reload(): Promise<void> {
        await this.loadNotes();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.canAddNote = this.accountContext.hasPermission('CM.CaseNote.Insert.SummaryInsert');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected openNoteHistory(note: Core.Note) {
        if(!note.id) return;
        this.$noteHistory.note = note;

        this.$noteHistoryModal.open();
    }

    protected addNote(): void {
        if(!this.householdCaseId) { throw new Error('Missing required property: householdCaseId'); }
        this.$addNoteModal.open();
    }

    protected addNoteModalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$addNoteModal.close();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadNotes(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = [
            'category',
            'category.subCategory',
            'entered.byUser',
            'flags',
            'history',
            'metadata.activeChanged.byUser',
            'metadata.created.byTask',
            'metadata.created.byUser',
            'case',
            'case.member'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadNotesByTarget(this.householdCaseId, options, includes);
            this.notes = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
